// Procedurally generated sound effects via the Web Audio API. No asset files —
// every sound is synthesised, so the game stays fully self-contained. Volume is
// driven by config.masterVolume and the audio context is created lazily on the
// first user gesture (browsers require this). Positional sounds (mob groans) use
// a PannerNode so you can hear where a zombie is.

import { config } from '../sim/config';
import { Vec3 } from '../sim/math';

export type Sfx = 'break' | 'place' | 'step' | 'hurt' | 'mobhit' | 'pickup' | 'groan';

export class AudioManager {
  private ctx: AudioContext | null = null;

  /** Must be called from a user gesture (e.g. pointer lock click). */
  resume(): void {
    if (!this.ctx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  /** Update the 3D listener (player eye + look direction) for positional audio. */
  updateListener(pos: Vec3, forward: Vec3): void {
    const ctx = this.ctx;
    if (!ctx) return;
    const l = ctx.listener;
    if (l.positionX) {
      l.positionX.value = pos.x;
      l.positionY.value = pos.y;
      l.positionZ.value = pos.z;
      l.forwardX.value = forward.x;
      l.forwardY.value = forward.y;
      l.forwardZ.value = forward.z;
      l.upX.value = 0;
      l.upY.value = 1;
      l.upZ.value = 0;
    } else {
      // Deprecated fallback for older browsers.
      l.setPosition(pos.x, pos.y, pos.z);
      l.setOrientation(forward.x, forward.y, forward.z, 0, 1, 0);
    }
  }

  play(sfx: Sfx): void {
    const ctx = this.ctx;
    if (!ctx || config.masterVolume <= 0) return;
    this.emit(ctx, sfx, ctx.destination);
  }

  /** Play a sound anchored at a world position (heard directionally). */
  playPositional(sfx: Sfx, at: Vec3): void {
    const ctx = this.ctx;
    if (!ctx || config.masterVolume <= 0) return;
    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 4;
    panner.maxDistance = 40;
    panner.rolloffFactor = 1;
    if (panner.positionX) {
      panner.positionX.value = at.x;
      panner.positionY.value = at.y;
      panner.positionZ.value = at.z;
    } else {
      panner.setPosition(at.x, at.y, at.z);
    }
    panner.connect(ctx.destination);
    this.emit(ctx, sfx, panner);
  }

  private emit(ctx: AudioContext, sfx: Sfx, out: AudioNode): void {
    switch (sfx) {
      case 'break':
        this.noiseBurst(ctx, 0.18, 1400, 0.5, out);
        break;
      case 'place':
        this.tone(ctx, 'square', 160, 110, 0.12, 0.4, out);
        break;
      case 'step':
        this.noiseBurst(ctx, 0.06, 500, 0.18, out);
        break;
      case 'hurt':
        this.tone(ctx, 'sawtooth', 220, 70, 0.25, 0.5, out);
        break;
      case 'mobhit':
        this.tone(ctx, 'square', 320, 120, 0.18, 0.45, out);
        break;
      case 'pickup':
        this.tone(ctx, 'sine', 600, 1000, 0.1, 0.35, out);
        break;
      case 'groan':
        this.tone(ctx, 'sawtooth', 130, 85, 0.5, 0.5, out);
        break;
    }
  }

  private gain(ctx: AudioContext, peak: number, duration: number, out: AudioNode): GainNode {
    const g = ctx.createGain();
    const vol = peak * config.masterVolume;
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0001, vol), now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    g.connect(out);
    return g;
  }

  private tone(
    ctx: AudioContext,
    type: OscillatorType,
    fromHz: number,
    toHz: number,
    duration: number,
    peak: number,
    out: AudioNode,
  ): void {
    const osc = ctx.createOscillator();
    osc.type = type;
    const now = ctx.currentTime;
    osc.frequency.setValueAtTime(fromHz, now);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, toHz), now + duration);
    osc.connect(this.gain(ctx, peak, duration, out));
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  private noiseBurst(
    ctx: AudioContext,
    duration: number,
    cutoffHz: number,
    peak: number,
    out: AudioNode,
  ): void {
    const frames = Math.floor(ctx.sampleRate * duration);
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < frames; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoffHz;
    src.connect(filter);
    filter.connect(this.gain(ctx, peak, duration, out));
    src.start(ctx.currentTime);
  }
}
